import type {
    ServerNode,
    ServerEdge,
    ServerGraph,
    WorkflowMetadata,
    WorkflowPayload,
    WorkflowCreatePayload,
    WorkflowUpdatePayload,
} from "./types";
import type { StepDefinition } from "./workflowParser";
import type { ConditionNodeData } from "../nodes/ConditionNode";
import type { ActionNodeData } from "../nodes/ActionNode";
import { Trigger } from "../nodes/TriggerNode";
import { ACTION_CODE_TO_FORM_TYPE } from "../utils/actionCodeMapping";

const NODE_X_SPACING = 200;

/**
 * Converts the in-memory StepDefinition[] into the server workflow JSON payload.
 *
 * - For creation (no workflowId): returns WorkflowCreatePayload
 * - For update (has workflowId): returns WorkflowUpdatePayload
 *
 * Only configured (non-placeholder) steps are serialized.
 */
export function serializeStepsToWorkflow(
    steps: StepDefinition[],
    metadata: WorkflowMetadata,
): WorkflowPayload {
    const configuredSteps = steps.filter((s) => !s.data?.isPlaceholder);

    const nodes: ServerNode[] = [];
    const edges: ServerEdge[] = [];

    for (let i = 0; i < configuredSteps.length; i++) {
        const step = configuredSteps[i];

        nodes.push({
            id: step.id,
            type: resolveServerNodeType(step),
            config: resolveServerConfig(step),
            position: { x: i * NODE_X_SPACING, y: 0 },
        });

        if (i + 1 < configuredSteps.length) {
            const nextId = configuredSteps[i + 1].id;
            const edgeWhen = step.type === "condition" ? ("TRUE" as const) : ("ALWAYS" as const);
            edges.push({ from: step.id, to: nextId, when: edgeWhen });
        }

        if (step.type === "condition") {
            edges.push({ from: step.id, to: null, when: "FALSE" });
        }
    }

    const entryNodeId = configuredSteps.length > 0 ? configuredSteps[0].id : "";

    const graph: ServerGraph = {
        entryNodeId,
        nodes,
        edges,
    };

    if (metadata.workflowId) {
        const payload: WorkflowUpdatePayload = {
            workflowId: metadata.workflowId,
            name: metadata.name,
            triggerType: metadata.triggerType,
            applyOncePerContact: metadata.applyOncePerContact ?? false,
            status: metadata.status,
            revision: metadata.revision,
            graph,
        };
        return payload;
    }

    const payload: WorkflowCreatePayload = {
        name: metadata.name,
        triggerType: metadata.triggerType,
        applyOncePerContact: metadata.applyOncePerContact ?? false,
        graph,
    };
    return payload;
}

// ---------------------------------------------------------------------------
// Step → server node type resolution
// ---------------------------------------------------------------------------

function resolveServerNodeType(step: StepDefinition): string {
    if (step.type === "trigger") {
        return step.data?.triggerType === Trigger.Message.Outbound
            ? "Trigger.Message.Outbound"
            : "Trigger.Message.Inbound";
    }

    if (step.type === "condition") {
        const data = step.data as ConditionNodeData;
        return data.conditionCode ?? "Condition.Message.KeywordMatch";
    }

    const data = step.data as ActionNodeData;
    if (data.actionCode) return data.actionCode;

    const formTypeToCode = Object.fromEntries(
        Object.entries(ACTION_CODE_TO_FORM_TYPE).map(([code, formType]) => [formType, code]),
    );
    return formTypeToCode[data.actionType ?? ""] ?? "Action.Message.AutomatedResponse";
}

// ---------------------------------------------------------------------------
// Step data → server config resolution
// ---------------------------------------------------------------------------

function resolveServerConfig(step: StepDefinition): Record<string, unknown> {
    if (step.type === "trigger") return {};
    if (step.type === "condition") return serializeConditionConfig(step.data as ConditionNodeData);
    return serializeActionConfig(step.data as ActionNodeData);
}

/**
 * ConditionNodeData → server config.
 * Reverse of conditionHydrator field-name translations.
 */
function serializeConditionConfig(data: ConditionNodeData): Record<string, unknown> {
    switch (data.conditionCode) {
        case "Condition.Message.KeywordMatch":
            return {
                keywords: data.keywords ?? [],
                matchMode: data.matchType ?? "Contains",
            };

        case "Condition.Time.RunSchedule":
            const scheduleConfig: Record<string, unknown> = {
                schedule: data.runSchedule ?? "24_7",
            };
            if (data.runSchedule === "SpecificHours" && data.businessHours) {
                scheduleConfig.timeZone = data.businessHours.timeZone;
                scheduleConfig.businessDays = data.businessHours.businessDays;
            }
            return scheduleConfig;

        case "Condition.Location.Countries":
            return {
                countries: data.countries ?? [],
            };

        case "Condition.Message.NumberMatch":
            return {
                numbers: data.phoneNumbers ?? [],
            };

        default:
            return {};
    }
}

/**
 * ActionNodeData → server config.
 * Reverse of actionHydrator field-name translations.
 */
function serializeActionConfig(data: ActionNodeData): Record<string, unknown> {
    switch (data.actionCode) {
        case "Action.Message.AutomatedResponse":
            return {
                from: "",
                pageId: "",
                to: "",
                messageBody: data.message ?? "",
                mediaFileList: data.mediaFileList ?? [],
                userId: "",
                associatedObjectId: "",
                associatedObjectType: "",
                templateId: data.templateId ?? "",
                components: data.components,
                fieldNameAndValueMap: data.fieldNameAndValueMap,
            };

        case "Action.Crm.CreateLead":
            return {};

        case "Action.Crm.AddNote":
            return {
                note: data.logRecordsFor ?? "all",
                shouldThreadActivities: data.logRecordAsThread === "true",
            };

        case "Action.Crm.LogActivity":
            return {
                note: data.logRecordsFor ?? "all",
                activityType: "HelloSend SMS",
                shouldThreadActivities: data.logRecordAsThread === "true",
            };

        case "Action.Notify.NotifyUser":
            return {
                userIds: [],
                includeAccountOwner: data.accountOwner ?? false,
                includeRecordOwner: data.recordOwner ?? false,
                customEmails: data.includeAdditionalEmails ? (data.additionalEmails ?? []) : [],
            };

        default:
            return {};
    }
}
